import type { User } from "@supabase/supabase-js";
import type {
  AiOption,
  AiPlan,
  AiWish,
  AppData,
  CatchLog,
  Experience,
  ExperienceInput,
  ExperienceStepInput,
  ExperienceVisibility,
  FishingLocation,
  FishingTrip,
  GardenIdea,
  GardenPlanting,
  GardenZone,
  LoadResult,
  Member,
  MembershipRole,
  ModuleKey,
  PlantSpecies,
  Reflection,
  WaterObservation,
  Workspace,
} from "../types";
import { supabase } from "./supabase";

type Row = Record<string, any>;

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

function clean(value: unknown, maxLength = 1000) {
  return typeof value === "string" ? value.trim().slice(0, maxLength) : "";
}

async function currentUser() {
  const { data, error } = await supabase.auth.getUser();
  throwIfError(error);
  if (!data.user) throw new Error("Innskráningin er útrunnin.");
  return data.user;
}

export async function claimInvitations(user: User) {
  const displayName =
    clean(user.user_metadata?.full_name, 100) ||
    clean(user.user_metadata?.name, 100) ||
    user.email?.split("@")[0] ||
    "Notandi";
  const { error } = await supabase.rpc("claim_workspace_invitations", {
    p_display_name: displayName,
  });
  throwIfError(error);
}

async function loadMembership(userId: string) {
  const { data, error } = await supabase
    .from("workspace_memberships")
    .select("workspace_id, role")
    .eq("user_id", userId)
    .eq("status", "active")
    .order("joined_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  throwIfError(error);
  return data as { workspace_id: string; role: MembershipRole } | null;
}

export async function loadAppData(user: User): Promise<LoadResult> {
  await claimInvitations(user);
  const membership = await loadMembership(user.id);
  if (!membership) return { kind: "onboarding" };

  const { data: workspaceRow, error: workspaceError } = await supabase
    .from("workspaces")
    .select("id, name, slug")
    .eq("id", membership.workspace_id)
    .single();
  throwIfError(workspaceError);
  const workspace = workspaceRow as Workspace;

  const [moduleResult, memberResult, experienceResult] = await Promise.all([
    supabase
      .from("workspace_modules")
      .select("module_key")
      .eq("workspace_id", workspace.id)
      .in("status", ["trial", "active"]),
    supabase
      .from("workspace_memberships")
      .select("user_id, role")
      .eq("workspace_id", workspace.id)
      .eq("status", "active")
      .order("joined_at"),
    supabase
      .from("experiences")
      .select("*")
      .eq("workspace_id", workspace.id)
      .order("event_date", { ascending: false })
      .order("created_at", { ascending: false }),
  ]);
  throwIfError(moduleResult.error);
  throwIfError(memberResult.error);
  throwIfError(experienceResult.error);

  const modules = (moduleResult.data ?? []).map(
    (row: Row) => row.module_key as ModuleKey,
  );
  const membershipRows = (memberResult.data ?? []) as Row[];
  const memberIds = membershipRows.map((row) => row.user_id as string);
  const { data: profileRows, error: profileError } = memberIds.length
    ? await supabase
        .from("profiles")
        .select("id, email, display_name")
        .in("id", memberIds)
    : { data: [], error: null };
  throwIfError(profileError);
  const profiles = new Map(
    ((profileRows ?? []) as Row[]).map((row) => [row.id as string, row]),
  );
  const members: Member[] = membershipRows.map((row) => {
    const profile = profiles.get(row.user_id as string);
    return {
      userId: row.user_id,
      displayName: profile?.display_name || profile?.email || "Meðlimur",
      email: profile?.email || "",
      role: row.role,
    };
  });

  const experienceRows = (experienceResult.data ?? []) as Row[];
  const experienceIds = experienceRows.map((row) => row.id as string);
  const [stepResult, noteResult, reflectionResult] = experienceIds.length
    ? await Promise.all([
        supabase
          .from("experience_steps")
          .select("*")
          .in("experience_id", experienceIds)
          .order("sort_order"),
        supabase
          .from("experience_private_notes")
          .select("*")
          .in("experience_id", experienceIds),
        supabase
          .from("reflections")
          .select("*")
          .in("experience_id", experienceIds)
          .order("created_at"),
      ])
    : [
        { data: [], error: null },
        { data: [], error: null },
        { data: [], error: null },
      ];
  throwIfError(stepResult.error);
  throwIfError(noteResult.error);
  throwIfError(reflectionResult.error);
  const steps = (stepResult.data ?? []) as Row[];
  const privateNotes = (noteResult.data ?? []) as Row[];
  const reflections = (reflectionResult.data ?? []) as Row[];
  const names = new Map(members.map((member) => [member.userId, member.displayName]));

  const experiences: Experience[] = experienceRows.map((row) => {
    const isOrganizer = row.created_by === user.id;
    const reflectionRows = reflections.filter(
      (reflection) => reflection.experience_id === row.id,
    );
    return {
      id: row.id,
      workspaceId: row.workspace_id,
      createdBy: row.created_by,
      title: row.title,
      occasion: row.occasion,
      eventDate: row.event_date,
      endDate: row.end_date ?? null,
      status: row.status,
      visibility: row.visibility,
      revealedAt: row.revealed_at,
      introText: row.intro_text,
      closingText: row.closing_text,
      privateNote:
        privateNotes.find(
          (note) => note.experience_id === row.id && note.step_id === null,
        )?.note ?? null,
      isOrganizer,
      canManage:
        isOrganizer ||
        membership.role === "owner" ||
        membership.role === "admin",
      canReveal:
        isOrganizer && row.visibility === "surprise" && !row.revealed_at,
      steps: steps
        .filter((step) => step.experience_id === row.id)
        .map((step) => ({
          id: step.id,
          experienceId: step.experience_id,
          sortOrder: step.sort_order,
          dayDate: step.day_date ?? null,
          timeLabel: step.time_label,
          chapter: step.chapter,
          title: step.title,
          note: step.note,
          privateNote:
            privateNotes.find((note) => note.step_id === step.id)?.note ?? null,
        })),
      reflections: reflectionRows.map(
        (reflection): Reflection => ({
          id: reflection.id,
          experienceId: reflection.experience_id,
          authorUserId: reflection.author_user_id,
          authorName: names.get(reflection.author_user_id) ?? "Meðlimur",
          rating: reflection.rating,
          bestPart: reflection.best_part,
          feeling: reflection.feeling,
          pace: reflection.pace,
          repeatWish: reflection.repeat_wish,
          changeNext: reflection.change_next,
          memory: reflection.memory,
          isMine: reflection.author_user_id === user.id,
        }),
      ),
    };
  });

  let gardenZones: GardenZone[] = [];
  let plantSpecies: PlantSpecies[] = [];
  let gardenPlantings: GardenPlanting[] = [];
  let gardenIdeas: GardenIdea[] = [];
  if (modules.includes("garden")) {
    const [zonesResult, speciesResult, plantingResult, ideaResult] =
      await Promise.all([
        supabase
          .from("garden_zones")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("sort_order"),
        supabase.from("plant_species").select("*").order("common_name"),
        supabase
          .from("garden_plantings")
          .select("*")
          .eq("workspace_id", workspace.id),
        supabase
          .from("garden_ideas")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("created_at", { ascending: false }),
      ]);
    [zonesResult, speciesResult, plantingResult, ideaResult].forEach((result) =>
      throwIfError(result.error),
    );
    gardenZones = ((zonesResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      name: row.name,
      symbol: row.symbol,
      description: row.description,
      dimensions: row.dimensions,
      status: row.status,
      sortOrder: row.sort_order,
    }));
    plantSpecies = ((speciesResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      commonName: row.common_name,
      scientificName: row.scientific_name,
      soil: row.soil,
      light: row.light,
      shelter: row.shelter,
      minHeightCm: row.min_height_cm,
      maxHeightCm: row.max_height_cm,
      floweringStartMonth: row.flowering_start_month,
      floweringEndMonth: row.flowering_end_month,
      colors: row.colors ?? [],
      notes: row.notes,
    }));
    gardenPlantings = ((plantingResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      zoneId: row.zone_id,
      plantSpeciesId: row.plant_species_id,
      positionNote: row.position_note,
      status: row.status,
    }));
    gardenIdeas = ((ideaResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      authorUserId: row.author_user_id,
      authorName: names.get(row.author_user_id) ?? "Meðlimur",
      text: row.text,
      status: row.status,
      createdAt: row.created_at,
    }));
  }

  let fishingLocations: FishingLocation[] = [];
  let fishingTrips: FishingTrip[] = [];
  let catchLogs: CatchLog[] = [];
  let waterObservations: WaterObservation[] = [];
  if (modules.includes("fishing")) {
    const [locationResult, tripResult, catchResult, observationResult] =
      await Promise.all([
        supabase
          .from("fishing_locations")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("name"),
        supabase
          .from("fishing_trips")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("starts_at", { ascending: false }),
        supabase
          .from("catch_logs")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("caught_at", { ascending: false }),
        supabase
          .from("water_observations")
          .select("*")
          .eq("workspace_id", workspace.id)
          .order("observed_at", { ascending: false }),
      ]);
    [locationResult, tripResult, catchResult, observationResult].forEach(
      (result) => throwIfError(result.error),
    );
    fishingLocations = ((locationResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      name: row.name,
      waterSystem: row.water_system,
      mapUrl: row.map_url,
      notes: row.notes,
    }));
    fishingTrips = ((tripResult.data ?? []) as Row[]).map((row) => ({
      id: row.id,
      locationId: row.location_id,
      startsAt: row.starts_at,
      endsAt: row.ends_at,
      weatherSummary: row.weather_summary,
      waterLevel: row.water_level,
      waterColor: row.water_color,
      notes: row.notes,
    }));
    const locationNames = new Map(
      fishingLocations.map((location) => [location.id, location.name]),
    );
    const trips = new Map(fishingTrips.map((trip) => [trip.id, trip]));
    catchLogs = ((catchResult.data ?? []) as Row[]).map((row) => {
      const trip = row.trip_id ? trips.get(row.trip_id) : undefined;
      return {
        id: row.id,
        locationName:
          (trip?.locationId && locationNames.get(trip.locationId)) ||
          "Óskráður staður",
        species: row.species,
        caughtAt: row.caught_at,
        lengthCm: row.length_cm,
        weightKg: row.weight_kg,
        lure: row.lure,
        released: row.released,
        notes: row.notes,
      };
    });
    waterObservations = ((observationResult.data ?? []) as Row[]).map(
      (row) => ({
        id: row.id,
        locationId: row.location_id,
        locationName:
          (row.location_id && locationNames.get(row.location_id)) ||
          "Óskráður staður",
        observedAt: row.observed_at,
        tidePhase: row.tide_phase,
        waterLevel: row.water_level,
        waterColor: row.water_color,
        flow: row.flow,
        weather: row.weather,
        notes: row.notes,
      }),
    );
  }

  const data: AppData = {
    workspace,
    role: membership.role,
    modules,
    members,
    experiences,
    gardenZones,
    plantSpecies,
    gardenPlantings,
    gardenIdeas,
    fishingLocations,
    fishingTrips,
    catchLogs,
    waterObservations,
  };
  return { kind: "ready", data };
}

export async function createWorkspace(name: string) {
  const { error } = await supabase.rpc("create_workspace", {
    p_name: clean(name, 100),
  });
  throwIfError(error);
}

export async function inviteMember(
  workspaceId: string,
  email: string,
  role: "admin" | "member" | "guest" = "member",
) {
  const { error } = await supabase.rpc("invite_workspace_member", {
    p_workspace_id: workspaceId,
    p_email: email.trim().toLowerCase(),
    p_role: role,
  });
  throwIfError(error);
}

function endDateFor(eventDate: string, endDate: string | null) {
  return endDate && endDate > eventDate ? endDate : null;
}

export async function createExperience(
  input: ExperienceInput & {
    workspaceId: string;
    visibility: ExperienceVisibility;
  },
) {
  const user = await currentUser();
  const { error } = await supabase.from("experiences").insert({
    workspace_id: input.workspaceId,
    created_by: user.id,
    title: clean(input.title, 120),
    occasion: clean(input.occasion, 80),
    event_date: input.eventDate,
    end_date: endDateFor(input.eventDate, input.endDate),
    visibility: input.visibility,
    intro_text: clean(input.introText, 1200),
  });
  throwIfError(error);
}

export async function updateExperience(experienceId: string, input: ExperienceInput) {
  const { error } = await supabase
    .from("experiences")
    .update({
      title: clean(input.title, 120),
      occasion: clean(input.occasion, 80),
      event_date: input.eventDate,
      end_date: endDateFor(input.eventDate, input.endDate),
      intro_text: clean(input.introText, 1200),
    })
    .eq("id", experienceId);
  throwIfError(error);
}

function stepRow(input: ExperienceStepInput) {
  return {
    day_date: input.dayDate || null,
    time_label: clean(input.timeLabel, 40),
    chapter: clean(input.chapter, 80),
    title: clean(input.title, 160),
    note: clean(input.note, 1200),
  };
}

export async function addExperienceStep(experience: Experience, input: ExperienceStepInput) {
  const nextOrder =
    experience.steps.reduce((max, step) => Math.max(max, step.sortOrder), 0) + 1;
  const { error } = await supabase.from("experience_steps").insert({
    experience_id: experience.id,
    sort_order: nextOrder,
    ...stepRow(input),
  });
  throwIfError(error);
}

export async function updateExperienceStep(stepId: string, input: ExperienceStepInput) {
  const { error } = await supabase
    .from("experience_steps")
    .update(stepRow(input))
    .eq("id", stepId);
  throwIfError(error);
}

export async function deleteExperienceStep(stepId: string) {
  const { error } = await supabase.from("experience_steps").delete().eq("id", stepId);
  throwIfError(error);
}

export async function reorderExperienceSteps(experienceId: string, stepIds: string[]) {
  const { error } = await supabase.rpc("reorder_experience_steps", {
    p_experience_id: experienceId,
    p_step_ids: stepIds,
  });
  throwIfError(error);
}

async function askPlanner<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("skipuleggja-upplifun", { body });
  if (error) {
    const detail = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(detail?.error || "Gervigreindin svaraði ekki. Reyndu aftur.");
  }
  return data as T;
}

export async function suggestTrips(wish: AiWish) {
  const result = await askPlanner<{ kostir: AiOption[] }>({ mode: "tillogur", ...wish });
  return result.kostir ?? [];
}

export function planTrip(wish: AiWish, option: AiOption) {
  return askPlanner<AiPlan>({
    mode: "aaetlun",
    osk: wish.osk,
    ferdalangar: wish.ferdalangar,
    valkostur: option,
    fraDagur: option.dagsetningar.fra,
    tilDagur: option.dagsetningar.til,
  });
}

function withLink(text: string, url: string) {
  return [text, url].filter(Boolean).join("\n");
}

export async function saveTripPlan(
  workspaceId: string,
  wish: AiWish,
  option: AiOption,
  plan: AiPlan,
) {
  const user = await currentUser();
  const start = option.dagsetningar.fra;
  const { data: experience, error } = await supabase
    .from("experiences")
    .insert({
      workspace_id: workspaceId,
      created_by: user.id,
      title: clean(plan.titill || option.borg, 120),
      occasion: "Ferðalag",
      event_date: start,
      end_date: endDateFor(start, option.dagsetningar.til),
      status: "planned",
      visibility: "shared",
      intro_text: clean(plan.inngangur || wish.osk, 1200),
    })
    .select("id")
    .single();
  throwIfError(error);
  if (!experience) throw new Error("Gat ekki vistað upplifunina.");

  const flight = option.flug;
  const steps = [
    {
      day_date: start,
      time_label: "",
      chapter: "Flug",
      title: `Flug til ${option.borg}`,
      note: withLink(
        [flight.flugfelog, flight.flugtimi, flight.beint ? "beint" : "með stoppi", flight.verdhugmynd]
          .filter(Boolean)
          .join(" · "),
        flight.bokunarslod,
      ),
    },
    {
      day_date: start,
      time_label: "",
      chapter: "Gisting",
      title: "Hótel – veljið eitt",
      note: plan.hotel
        .map((hotel) => withLink(`${hotel.nafn} (${hotel.hverfi}) · ${hotel.verdhugmynd}\n${hotel.afhverju}`, hotel.slod))
        .join("\n\n"),
    },
    ...plan.dagar.flatMap((day) =>
      day.lidir.map((item) => ({
        day_date: day.dagur,
        time_label: item.klukkan,
        chapter: item.kafli,
        title: item.titill,
        note: withLink(item.lysing, item.slod),
      })),
    ),
  ].map((step, index) => ({
    experience_id: experience.id,
    sort_order: index + 1,
    day_date: step.day_date || null,
    time_label: clean(step.time_label, 40),
    chapter: clean(step.chapter, 80),
    title: clean(step.title, 160) || "Liður",
    note: clean(step.note, 1200),
  }));
  const { error: stepError } = await supabase.from("experience_steps").insert(steps);
  throwIfError(stepError);
  return experience.id as string;
}

export async function revealExperience(experienceId: string) {
  const { error } = await supabase.rpc("reveal_experience", {
    p_experience_id: experienceId,
  });
  throwIfError(error);
}

export async function saveReflection(
  experienceId: string,
  input: {
    rating: number;
    bestPart: string;
    feeling: string;
    pace: string;
    repeatWish: string;
    changeNext: string;
    memory: string;
  },
) {
  const user = await currentUser();
  const { error } = await supabase.from("reflections").upsert(
    {
      experience_id: experienceId,
      author_user_id: user.id,
      rating: Math.max(1, Math.min(5, Math.round(input.rating))),
      best_part: clean(input.bestPart, 800),
      feeling: clean(input.feeling, 500),
      pace: clean(input.pace, 240),
      repeat_wish: clean(input.repeatWish, 500),
      change_next: clean(input.changeNext, 500),
      memory: clean(input.memory, 1000),
    },
    { onConflict: "experience_id,author_user_id" },
  );
  throwIfError(error);
}

export async function addGardenIdea(workspaceId: string, text: string) {
  const user = await currentUser();
  const { error } = await supabase.from("garden_ideas").insert({
    workspace_id: workspaceId,
    author_user_id: user.id,
    text: clean(text, 800),
  });
  throwIfError(error);
}

export async function addWaterObservation(input: {
  workspaceId: string;
  locationId: string;
  observedAt: string;
  tidePhase: string;
  waterLevel: string;
  waterColor: string;
  flow: string;
  weather: string;
  notes: string;
}) {
  const user = await currentUser();
  const { error } = await supabase.from("water_observations").insert({
    workspace_id: input.workspaceId,
    location_id: input.locationId || null,
    author_user_id: user.id,
    observed_at: new Date(input.observedAt).toISOString(),
    tide_phase: clean(input.tidePhase, 100),
    water_level: clean(input.waterLevel, 100),
    water_color: clean(input.waterColor, 100),
    flow: clean(input.flow, 100),
    weather: clean(input.weather, 240),
    notes: clean(input.notes, 1000),
  });
  throwIfError(error);
}

export async function addQuickCatch(input: {
  workspaceId: string;
  locationId: string;
  caughtAt: string;
  species: string;
  lengthCm: number | null;
  weightKg: number | null;
  lure: string;
  released: boolean;
  notes: string;
}) {
  const user = await currentUser();
  const caughtAt = new Date(input.caughtAt).toISOString();
  const { data: trip, error: tripError } = await supabase
    .from("fishing_trips")
    .insert({
      workspace_id: input.workspaceId,
      location_id: input.locationId || null,
      created_by: user.id,
      starts_at: caughtAt,
      notes: "Hraðskráning úr aflaskrá.",
    })
    .select("id")
    .single();
  throwIfError(tripError);
  if (!trip) throw new Error("Gat ekki stofnað veiðiferð fyrir aflaskrána.");
  const { error } = await supabase.from("catch_logs").insert({
    workspace_id: input.workspaceId,
    trip_id: trip.id,
    created_by: user.id,
    species: clean(input.species, 100) || "Sjóbirtingur",
    caught_at: caughtAt,
    length_cm: input.lengthCm,
    weight_kg: input.weightKg,
    lure: clean(input.lure, 200),
    released: input.released,
    notes: clean(input.notes, 1000),
  });
  throwIfError(error);
}
